import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { useEffect } from 'react';

import { appActions } from 'app/app.actions';
import { getLastWsMessageAt } from 'middleware/websocket/lastWsMessage';
import { debugLog } from 'utils/debugLog';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

const HEALTH_CHECK_INTERVAL_MS = 60_000;
const STALE_THRESHOLD_MS = 180_000;

export const useWebSocketHealthCheck = (): void => {
  const dispatch = useAppDispatch();
  const asyncCreateStatus = useAppSelector(
    (state) => state.websocket.meta.asyncCreateStatus,
  );
  const { websocketHealthCheckModal: isModalEnabled } = useFeatureFlag([
    'websocket-health-check-modal',
  ]);

  useEffect(() => {
    if (asyncCreateStatus !== 'fulfilled') return;

    const intervalId = setInterval(() => {
      const lastMessageAt = getLastWsMessageAt();
      if (lastMessageAt === null) return;

      const staleDuration = Date.now() - lastMessageAt;
      if (staleDuration < STALE_THRESHOLD_MS) return;

      clearInterval(intervalId);

      debugLog(
        'WS-HEALTH-CHECK',
        `Connection stale for ${Math.round(staleDuration / 1000)}s`,
      );
      Sentry.captureMessage('WebSocket: silently dead connection detected', {
        level: 'warning',
        tags: { team: SentryTeam.Platform },
        extra: { staleDurationMs: staleDuration },
      });

      if (isModalEnabled) {
        dispatch(
          appActions.showModal({
            modal: { type: 'app:webSocketHealthCheck' },
          }),
        );
      }
    }, HEALTH_CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [asyncCreateStatus, dispatch, isModalEnabled]);
};
